import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import { EditOutlined, AddCircleOutline, Close } from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { adminService } from "../services/adminService";
import {
  primaryDark,
  accentColor,
  buttonGradient,
  buttonGradientHover,
} from "../../../constants/consts";

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
`;

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    transition: "box-shadow 0.25s ease, background-color 0.25s ease",
    "&:hover": { backgroundColor: "rgba(4,87,129,0.02)" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(4,87,129,0.10)" },
  },
};

export default function KnowledgeEditDialog({ open, item, onClose, onSaved }) {
  const [form, setForm] = useState({ id: "", title: "", description: "" });
  const [imageUrl, setImageUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && item) {
      setForm({
        id: item.id ?? "",
        title: item.title ?? "",
        description: item.description ?? "",
      });
      setImageUrl(item.image ?? "");
      setPreviewUrl(item.image ?? "");
      setFile(null);
      setError("");
    }
  }, [open, item]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      setError("Konu ve içerik zorunludur.");
      return;
    }
    setSaving(true);
    setError("");

    try {
      let finalImage = imageUrl;

      // Yeni resim seçildiyse yükle
      if (file) {
        setUploading(true);
        const res = await adminService.uploadFile(
          file,
          "BilgiBankasi",
          "image",
        );
        setUploading(false);
        const url = res?.url;
        if (!url) throw new Error("Resim yüklenemedi");
        finalImage = url;
      }

      await adminService.editKnowledge({
        id: form.id,
        title: form.title.trim(),
        description: form.description.trim(),
        image: finalImage,
      });

      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      setError("Kayıt güncellenemedi.");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          animation: `${popIn} 0.25s cubic-bezier(0.22, 1, 0.36, 1) both`,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 1.75,
          background: `linear-gradient(135deg, ${primaryDark} 0%, ${accentColor} 100%)`,
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            color: "#fff",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EditOutlined sx={{ fontSize: 20 }} />
          Kaydı Düzenle
        </DialogTitle>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {/* Resim önizleme + değiştir */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Box
            sx={{
              position: "relative",
              width: 140,
              height: 140,
              borderRadius: 2,
              backgroundColor: "#F8FAFC",
              border: "2px dashed rgba(4,87,129,0.20)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {previewUrl ? (
              <Box
                component="img"
                src={previewUrl}
                alt="preview"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Typography sx={{ color: "#94A3B8", fontSize: 12 }}>
                Resim yok
              </Typography>
            )}

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleFile}
            />

            <IconButton
              size="small"
              onClick={() => fileRef.current?.click()}
              disabled={saving}
              sx={{
                position: "absolute",
                bottom: 6,
                right: 6,
                backgroundColor: "rgba(255,255,255,0.95)",
                color: "#15803D",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": { backgroundColor: "#fff" },
              }}
            >
              <AddCircleOutline sx={{ fontSize: 20 }} />
            </IconButton>

            {previewUrl && previewUrl !== imageUrl && (
              <IconButton
                size="small"
                onClick={() => {
                  setFile(null);
                  setPreviewUrl(imageUrl);
                }}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  backgroundColor: "rgba(255,255,255,0.95)",
                  color: "#B91C1C",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  "&:hover": { backgroundColor: "#fff" },
                }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Konu *"
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            fullWidth
            size="small"
            sx={inputSx}
          />
          <TextField
            label="İçerik *"
            value={form.description}
            onChange={(e) =>
              setForm((p) => ({ ...p, description: e.target.value }))
            }
            fullWidth
            multiline
            minRows={6}
            maxRows={14}
            size="small"
            sx={inputSx}
          />
        </Box>

        {error && (
          <Typography
            sx={{ mt: 2, color: "#B91C1C", fontSize: 12.5, fontWeight: 600 }}
          >
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Vazgeç
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || uploading}
          variant="contained"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 700,
            background: buttonGradient,
            "&:hover": { background: buttonGradientHover },
            "&:disabled": { background: "#c7c7c7" },
          }}
        >
          {saving || uploading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Kaydet"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
